import { LOCALES, LOCALE_DIRECTION, type Locale } from '@fuel-system/contracts';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import arAuth from '../locales/ar/auth.json';
import arPhone from '../locales/ar/phone.json';
import arCategories from '../locales/ar/categories.json';
import arClosesNs from '../locales/ar/closes.json';
import arPendingNs from '../locales/ar/pending.json';
import arCommon from '../locales/ar/common.json';
import arDebtors from '../locales/ar/debtors.json';
import arErrors from '../locales/ar/errors.json';
import arGroups from '../locales/ar/groups.json';
import arLayout from '../locales/ar/layout.json';
import arMessages from '../locales/ar/messages.json';
import arNationalities from '../locales/ar/nationalities.json';
import arOverview from '../locales/ar/overview.json';
import arPdaCustomers from '../locales/ar/pdaCustomers.json';
import arReceipt from '../locales/ar/receipt.json';
import arSettings from '../locales/ar/settings.json';
import arTransactions from '../locales/ar/transactions.json';
import arTrash from '../locales/ar/trash.json';
import arUsers from '../locales/ar/users.json';
import arValidation from '../locales/ar/validation.json';
import arYears from '../locales/ar/years.json';
import enAuth from '../locales/en/auth.json';
import enPhone from '../locales/en/phone.json';
import enCategories from '../locales/en/categories.json';
import enClosesNs from '../locales/en/closes.json';
import enPendingNs from '../locales/en/pending.json';
import enCommon from '../locales/en/common.json';
import enDebtors from '../locales/en/debtors.json';
import enErrors from '../locales/en/errors.json';
import enGroups from '../locales/en/groups.json';
import enLayout from '../locales/en/layout.json';
import enMessages from '../locales/en/messages.json';
import enNationalities from '../locales/en/nationalities.json';
import enOverview from '../locales/en/overview.json';
import enPdaCustomers from '../locales/en/pdaCustomers.json';
import enReceipt from '../locales/en/receipt.json';
import enSettings from '../locales/en/settings.json';
import enTransactions from '../locales/en/transactions.json';
import enTrash from '../locales/en/trash.json';
import enUsers from '../locales/en/users.json';
import enValidation from '../locales/en/validation.json';
import enYears from '../locales/en/years.json';

export const DEFAULT_LOCALE: Locale = 'ar';
export const STORAGE_KEY = 'app_language';

/**
 * Arabic is the product's default language, not a translation of an English
 * original: `ar` is both the fallback and the initial value, so a missing key
 * degrades to Arabic rather than to English.
 */
export const resources = {
  ar: {
    phone: arPhone,
    common: arCommon,
    auth: arAuth,
    errors: arErrors,
    layout: arLayout,
    validation: arValidation,
    overview: arOverview,
    transactions: arTransactions,
    debtors: arDebtors,
    closes: arClosesNs,
    pending: arPendingNs,
    messages: arMessages,
    trash: arTrash,
    settings: arSettings,
    categories: arCategories,
    years: arYears,
    users: arUsers,
    groups: arGroups,
    receipt: arReceipt,
    pdaCustomers: arPdaCustomers,
    nationalities: arNationalities,
  },
  en: {
    phone: enPhone,
    common: enCommon,
    auth: enAuth,
    errors: enErrors,
    layout: enLayout,
    validation: enValidation,
    overview: enOverview,
    transactions: enTransactions,
    debtors: enDebtors,
    closes: enClosesNs,
    pending: enPendingNs,
    messages: enMessages,
    trash: enTrash,
    settings: enSettings,
    categories: enCategories,
    years: enYears,
    users: enUsers,
    groups: enGroups,
    receipt: enReceipt,
    pdaCustomers: enPdaCustomers,
    nationalities: enNationalities,
  },
} as const;

export const NAMESPACES = [
  'phone',
  'common',
  'auth',
  'errors',
  'layout',
  'validation',
  'overview',
  'transactions',
  'debtors',
  'closes',
  'pending',
  'messages',
  'trash',
  'settings',
  'categories',
  'years',
  'users',
  'groups',
  'receipt',
  'pdaCustomers',
  'nationalities',
] as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...LOCALES],
    ns: [...NAMESPACES],
    defaultNS: 'common',
    detection: {
      order: ['localStorage', 'htmlTag', 'navigator'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      // React already escapes rendered values; double-escaping mangles Arabic
      // punctuation and quotes.
      escapeValue: false,
    },
    returnNull: false,
  });

/**
 * Applies the locale to the document. Called on boot and on every switch so
 * `dir`, `lang` and the stored preference can never disagree.
 */
export function applyLocale(locale: Locale): void {
  const root = document.documentElement;
  const fontFamily = locale === 'ar' ? 'Cairo, system-ui, sans-serif' : 'Inter, sans-serif';
  root.lang = locale;
  root.dir = LOCALE_DIRECTION[locale];
  root.setAttribute('data-locale', locale);
  root.style.fontFamily = fontFamily;
  // The browser tab names the product in the chosen language (glossary §6).
  document.title = i18n.t('layout:documentTitle', { lng: locale });

  if (document.body) {
    document.body.style.fontFamily = fontFamily;
  }
}

export async function changeLocale(locale: Locale): Promise<void> {
  await i18n.changeLanguage(locale);
  applyLocale(locale);
}

export function currentLocale(): Locale {
  const language = i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LOCALE;
  return (LOCALES as readonly string[]).includes(language) ? (language as Locale) : DEFAULT_LOCALE;
}

applyLocale(currentLocale());

export default i18n;
